import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import {
  Box, Typography, Stepper, Step, StepLabel, Button, Paper,
  Grid, TextField, FormControl, InputLabel, Select, MenuItem,
  Alert, CircularProgress,
  IconButton, Tooltip, InputAdornment, Chip,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import RefreshIcon from "@mui/icons-material/Refresh";
import DownloadIcon from "@mui/icons-material/Download";
import { API_BASE_URL } from "../../config";
import AppTextField, { getEmailValidationError, getUsernameValidationError } from "../../components/inputs/AppTextField";
import RequiredAsterisk from "../../components/form/RequiredAsterisk";
import { minContractEndDate, todayISODate, validateContractEnd } from "../../utils/contractDateValidation";
import { alpha } from "@mui/material/styles";
// ─── helpers ──────────────────────────────────────────────────────────────────

const STEPS = ["Cloud & Deploy", "Client Admin", "Licensing", "Review & Confirm"];

function genPassword(len = 12) {
  const chars = "abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789!@#$%";
  return Array.from({ length: len }, () => chars[Math.floor(Math.random() * chars.length)]).join("");
}

const HOSTING_OPTIONS = [
  {
    value: "shared",
    label: "Shared SaaS",
    hint: "Runs on ValidAIte infrastructure (multi-tenant). No .lic file.",
    color: "#0094FB",
  },
  {
    value: "dedicated",
    label: "Dedicated (customer cloud)",
    hint: "App runs in the customer’s AWS / GCP / Azure account. Issues a signed .lic file.",
    color: "#2e7d32",
  },
];

const CLOUD_OPTIONS = [
  { value: "aws",   label: "Amazon Web Services (AWS)",   color: "#FF9900" },
  { value: "gcp",   label: "Google Cloud Platform (GCP)", color: "#4285F4" },
  { value: "azure", label: "Microsoft Azure",             color: "#0089D6" },
  { value: "private", label: "Private Cloud",             color: "#607D8B" },
];

const TIER_OPTIONS = [
  { value: "tenant",      label: "Tenant",      seats: 10   },
  { value: "enterprise",  label: "Enterprise",  seats: 100  },
  { value: "multitenant", label: "Multitenant", seats: 1000 },
];

const REGION_OPTIONS = [{ value: "us-east-1", label: "us-east-1" }];

// ─── Step 1: Cloud & Deploy ───────────────────────────────────────────────────

function  StepCloud({ form, set, errors, setErrors }) {

  return (
    <Box>
      <Typography fontWeight={600} fontSize="15px" mb={2}>Hosting model</Typography>
      <Grid container spacing={2} mb={3}>
        {HOSTING_OPTIONS.map((h) => (
          <Grid item xs={12} sm={6} key={h.value}>
            <Paper
              onClick={() => set("hosting", h.value)}
              variant="outlined"
              sx={{
                p: 2, cursor: "pointer", borderRadius: 2,
                borderColor: form.hosting === h.value ? h.color : "#D9D9D9",
                borderWidth: form.hosting === h.value ? 2 : 1,
                bgcolor: form.hosting === h.value ? h.color + "0D" : "#FFFFFF",
                transition: "all 0.15s",
                "&:hover": { borderColor: h.color },
              }}
            >
              <Typography fontWeight={600} fontSize="14px" sx={{ color: form.hosting === h.value ? h.color : "#344054" }}>
                {h.label}
              </Typography>
              <Typography fontSize="12px" color="text.secondary" mt={0.75}>{h.hint}</Typography>
            </Paper>
          </Grid>
        ))}
      </Grid>

      <Typography fontWeight={600} fontSize="15px" mb={2}>Cloud provider</Typography>
      <Grid container spacing={2} mb={3}>
        {CLOUD_OPTIONS.map((c) => (
          <Grid item xs={12} sm={4} key={c.value}>
            <Paper
              onClick={() => set("cloud", c.value)}
              variant="outlined"
              sx={{
                p: 2, cursor: "pointer", borderRadius: 2,
                borderColor: form.cloud === c.value ? c.color : "#D9D9D9",
                borderWidth: form.cloud === c.value ? 2 : 1,
                bgcolor: form.cloud === c.value ? c.color + "0D" : "#FFFFFF",
                transition: "all 0.15s",
                "&:hover": { borderColor: c.color },
              }}
            >
              <Typography fontWeight={600} fontSize="14px" sx={{ color: form.cloud === c.value ? c.color : "#344054" }}>
                {c.label}
              </Typography>
            </Paper>
          </Grid>
        ))}
      </Grid>

      {(form.hosting === "dedicated" || form.cloud === "private") && (
        <TextField
          fullWidth
          size="small"
          label="Deployment fingerprint (optional)"
          value={form.deployment_fingerprint || ""}
          onChange={(e) => set("deployment_fingerprint", e.target.value.trim())}
          helperText="SHA256 fingerprint from the customer's GET /api/deployment/fingerprint. Bind the .lic to their installation."
          sx={{ mb: 2 }}
        />
      )}

      {form.hosting === "dedicated" && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Dedicated / Private Cloud tenants receive a signed <strong>.lic</strong> file for the customer deployment.
          Keycloak stays on ValidAIte; the app + data run in the customer account.
        </Alert>
      )}

      {form.cloud === "private" && form.hosting !== "dedicated" && (
        <Alert severity="info" sx={{ mb: 2 }} onClose={() => {}}>
          Private Cloud is treated as dedicated hosting — a signed <strong>.lic</strong> file will be issued.
        </Alert>
      )}

      {form.hosting === "shared" && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Shared SaaS uses platform DB contract + Keycloak for access control — no <strong>.lic</strong> file.
        </Alert>
      )}

      <Grid container spacing={2}>
        <Grid item xs={12} sm={6}>
          <AppTextField
            fullWidth
            label={<>Organisation Name <RequiredAsterisk /></>}
            value={form.org_name}
            onChange={(e) => set("org_name", e.target.value)}
            onErrorChange={(err) => setErrors((prev) => ({ ...prev, org_name: err }))}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <AppTextField
            fullWidth
            label={<>Realm Slug <RequiredAsterisk /> (URL-safe)</>}
            placeholder="acme-corp"
            value={form.slug}
            onChange={(e) => set("slug", e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, "-"))}
            onErrorChange={(err) => setErrors((p) => ({ ...p, slug: err }))}
            fieldType="slug"
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            select
            fullWidth
            size="small"
            label="Region"
            value={form.region}
            onChange={(e) => set("region", e.target.value)}
            InputLabelProps={{ shrink: true }}
          >
            <MenuItem value="">— Select —</MenuItem>
            {REGION_OPTIONS.map((r) => (
              <MenuItem key={r.value} value={r.value}>{r.label}</MenuItem>
            ))}
          </TextField>
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
  select
  fullWidth
  size="small"
  label="Industry"
  value={form.industry}
  onChange={(e) => set("industry", e.target.value)}
  InputLabelProps={{ shrink: true }}
  // Optional: add a helperText or character counter if you want
>
  <MenuItem value="">— Select —</MenuItem>
  {["Healthcare", "Finance", "Retail", "Manufacturing", "Education", "Government", "Technology", "Other"]
    .map((i) => <MenuItem key={i} value={i.toLowerCase()}>{i}</MenuItem>)}
</TextField>
        </Grid>
      </Grid>
    </Box>
  );
}

// ─── Step 2: Client Admin ─────────────────────────────────────────────────────

function StepAdmin({ form, set, setErrors }) {
  const [copied, setCopied] = useState(false);

  const copy = () => {
    navigator.clipboard.writeText(form.admin_temp_password);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <Box>
      <Typography fontWeight={600} fontSize="15px" mb={2}>Tenant Administrator Account</Typography>
      <Typography fontSize="13px" color="text.secondary" mb={2}>
        This user is created with the <strong>tenant-admin</strong> role and can invite others (including enterprise-ai-owner).
      </Typography>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6}>
          <AppTextField
            fullWidth
            label={<>First Name <RequiredAsterisk /></>}
            value={form.admin_first_name}
            onChange={(e) => set("admin_first_name", e.target.value)}
            onErrorChange={(err) => setErrors((p) => ({ ...p, admin_first_name: err }))}
            fieldType="name"
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <AppTextField
            fullWidth
            label={<>Last Name <RequiredAsterisk /></>}
            value={form.admin_last_name}
            onChange={(e) => set("admin_last_name", e.target.value)}
            onErrorChange={(err) => setErrors((p) => ({ ...p, admin_last_name: err }))}
            fieldType="name"
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <AppTextField
            fullWidth
            label={<>Email <RequiredAsterisk /></>}
            value={form.admin_email}
            onChange={(e) => set("admin_email", e.target.value)}
            onErrorChange={(err) => setErrors((p) => ({ ...p, admin_email: err }))}
            fieldType="email"
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <AppTextField
            fullWidth
            label="Username (defaults to email prefix)"
            placeholder={form.admin_email?.split("@")[0] || ""}
            value={form.admin_username}
            onChange={(e) => set("admin_username", e.target.value)}
            onErrorChange={(err) => setErrors((p) => ({ ...p, admin_username: err }))}
            fieldType="username"
          />
        </Grid>
        <Grid item xs={12}>
          <TextField fullWidth size="small" label="Temporary Password *"
            value={form.admin_temp_password}
            onChange={(e) => set("admin_temp_password", e.target.value)}
            helperText="The admin will be prompted to set a new password on first login."
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <Tooltip title={copied ? "Copied!" : "Copy password"}>
                    <IconButton size="small" onClick={copy}>
                      <ContentCopyIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Regenerate password">
                    <IconButton size="small" onClick={() => set("admin_temp_password", genPassword())}>
                      <RefreshIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </InputAdornment>
              ),
            }}
          />
        </Grid>
      </Grid>
    </Box>
  );
}

// ─── Step 3: Licensing ────────────────────────────────────────────────────────

function StepLicence({ form, set }) {
  const today = todayISODate();
  const contractStart = form.contract_start || today;
  const contractEndError = validateContractEnd(form.contract_end, contractStart);
  const contractEndMin = minContractEndDate(contractStart);

  return (
    <Box>
      <Typography fontWeight={600} fontSize="15px" mb={2}>Licence Configuration</Typography>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Typography fontSize="13px" color="text.secondary" mb={1}>Licence Tier</Typography>
          <Grid container spacing={2}>
            {TIER_OPTIONS.map((t) => (
              <Grid item xs={12} sm={4} key={t.value}>
                <Paper
                  onClick={() => { set("tier", t.value); set("seat_count", t.seats); }}
                  variant="outlined"
                  sx={{
                    p: 2, cursor: "pointer", textAlign: "center", borderRadius: 2,
                    borderColor: form.tier === t.value ? "#0094FB" : "#D9D9D9",
                    borderWidth: form.tier === t.value ? 2 : 1,
                    bgcolor: form.tier === t.value ? "#E8F3FF" : "#FFFFFF",
                    transition: "all 0.15s",
                    "&:hover": { borderColor: "#0094FB" },
                  }}
                >
                  <Typography fontWeight={700} fontSize="14px">{t.label}</Typography>
                  <Typography fontSize="12px" color="text.secondary" mt={0.5}>Up to {t.seats} seats</Typography>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField fullWidth size="small" label="Seat Count" type="number"
            value={form.seat_count}
            inputProps={{
              min: 1,
              max: TIER_OPTIONS.find(t => t.value === form.tier)?.seats || 10
            }}
            onChange={(e) => {
  const maxSeats =
    TIER_OPTIONS.find(t => t.value === form.tier)?.seats || 10;

  let value = e.target.value;

  // Allow empty input (so user can type)
  if (value === "") {
    set("seat_count", "");
    return;
  }

  let num = Number(value);

  if (!isNaN(num)) {
    if (num > maxSeats) num = maxSeats;
    if (num < 1) num = 1;

    set("seat_count", num);
  }
}}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField fullWidth size="small" label="Warning Threshold %" type="number"
            value={form.warning_threshold}
            onChange={(e) => set("warning_threshold", parseInt(e.target.value) || 80)}
            helperText="Alert when seat usage exceeds this %" />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField fullWidth size="small" label="Contract Start" type="date"
            value={contractStart}
            onChange={(e) => set("contract_start", e.target.value)}
            InputLabelProps={{ shrink: true }} />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            size="small"
            label="Contract End *"
            type="date"
            value={form.contract_end}
            onChange={(e) => set("contract_end", e.target.value)}
            InputLabelProps={{ shrink: true }}
            error={Boolean(form.contract_end && contractEndError)}
            helperText={
              form.contract_end && contractEndError
                ? contractEndError
                : "Must be today or a future date, and on or after contract start"
            }
            inputProps={{ min: contractEndMin }}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <FormControl fullWidth size="small">
            <InputLabel>On Expiry</InputLabel>
            <Select value={form.expiry_behaviour} onChange={(e) => set("expiry_behaviour", e.target.value)} label="On Expiry">
              <MenuItem value="lock">Lock Access</MenuItem>
              <MenuItem value="warn">Read-only mode</MenuItem>
              <MenuItem value="grace">7-Day Grace Period</MenuItem>
            </Select>
          </FormControl>
        </Grid>
      </Grid>
    </Box>
  );
}

// ─── Step 4: Review & Confirm ─────────────────────────────────────────────────

function ReviewRow({ label, value }) {
  return (
    <Box sx={{ display: "flex", justifyContent: "space-between", py: 0.75, borderBottom: "1px solid #f0f0f0" }}>
      <Typography fontSize="13px" color="text.secondary">{label}</Typography>
      <Typography fontSize="13px" fontWeight={500}>{value || "—"}</Typography>
    </Box>
  );
}

function StepReview({ form, result, licenceKey }) {
  const navigate = useNavigate();
  const downloadLic = () => {
    if (!licenceKey) return;
    const blob = new Blob([licenceKey], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${form.slug}.lic`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (result) {
    const loginUrl = result.loginUrl || `${window.location.origin}?realm=${form.slug}`;
    const emailSent = result.inviteEmailSent;
    const adminUsername = result.adminUsername;
    const adminTempPassword = result.adminTempPassword;
    const license = result.license;

    return (
      <Box sx={{ py: 2 }}>
        <Box sx={{ textAlign: "center", mb: 3 }}>
          <CheckCircleOutlineIcon sx={{ fontSize: 56, color: "primary.main", mb: 1 }} />
          <Typography variant="h6" fontWeight={700}>Tenant Created Successfully!</Typography>
          <Typography color="text.secondary" fontSize="14px" mt={0.5}>
            Keycloak realm <Chip label={form.slug} size="small" sx={{ mx: 0.5 }} /> is live.
          </Typography>
        </Box>

        {license && (
          <Box sx={{ border: "1px solid #e8e8e8", borderRadius: 2, p: 2.5, mb: 2, bgcolor: "#f9fafb" }}>
            <Typography fontSize="12px" color="text.secondary" mb={1}>Licence issued</Typography>
            <ReviewRow label="License ID" value={license.licenseId} />
            <ReviewRow label="Version" value={String(license.version)} />
            <ReviewRow label="Tier" value={license.tier} />
            <ReviewRow label="Seats" value={String(license.maxUsers)} />
            <ReviewRow label="Expires" value={license.expiresAt?.split("T")[0] || license.expiresAt} />
            {license.deploymentFingerprint && (
              <ReviewRow label="Fingerprint" value={`${license.deploymentFingerprint.slice(0, 16)}…`} />
            )}
          </Box>
        )}

        {/* Login URL */}
        <Box sx={{ border: "1px solid #e8e8e8", borderRadius: 2, p: 2.5, mb: 2, bgcolor: "#f9fafb" }}>
          <Typography fontSize="12px" color="text.secondary" mb={0.5}>Tenant Login URL</Typography>
          <Typography fontSize="13px" fontFamily="monospace" sx={{ color: "#0094FB", wordBreak: "break-all" }}>
            {loginUrl}
          </Typography>
        </Box>

        {/* Email sent */}
        {emailSent ? (
          <Box
            sx={{
              border: "1px solid",
              borderColor: "primary.main",
              borderRadius: 2,
              p: 2,
              mb: 2,
              bgcolor: (theme) => alpha(theme.palette.primary.main, 0.08),
            }}
          >
            <Typography fontSize="13px" color="primary.dark" fontWeight={600}>✓ Invitation email sent</Typography>
            <Typography fontSize="12px" color="text.secondary" mt={0.5}>
              A welcome email with login credentials was sent to <strong>{form.admin_email}</strong>. They will be prompted to set a new password on first login.
            </Typography>
          </Box>
        ) : (
          <Box sx={{ border: "1px solid #ffe0b2", borderRadius: 2, p: 2, mb: 2, bgcolor: "#fff8f0" }}>
            <Typography fontSize="13px" color="#e65100" fontWeight={600}>⚠ SMTP not configured — share credentials manually</Typography>
            <Typography fontSize="12px" color="text.secondary" mt={1} mb={0.5}>Username:</Typography>
            <Typography fontSize="13px" fontFamily="monospace" fontWeight={600}>{adminUsername}</Typography>
            <Typography fontSize="12px" color="text.secondary" mt={1} mb={0.5}>Temporary Password:</Typography>
            <Typography fontSize="13px" fontFamily="monospace" fontWeight={600}>{adminTempPassword}</Typography>
            <Typography fontSize="12px" color="text.secondary" mt={1}>
              Share these with <strong>{form.admin_email}</strong>. They will be prompted to change their password on first login.
            </Typography>
          </Box>
        )}

        {result.brokerRedirectUri && (
          <Box sx={{ border: "1px solid #e8e8e8", borderRadius: 2, p: 2.5, mb: 2, bgcolor: "#f9fafb" }}>
            <Typography fontSize="12px" color="text.secondary" mb={0.5}>
              OIDC Redirect URI — share with client&apos;s IdP admin
            </Typography>
            <Typography fontSize="12px" fontFamily="monospace" sx={{ color: "#0094FB", wordBreak: "break-all" }}>
              {result.brokerRedirectUri}
            </Typography>
          </Box>
        )}

        {licenceKey && (
          <Button variant="outlined" startIcon={<DownloadIcon />} onClick={downloadLic}
            sx={{ color: "#0094FB", borderColor: "#0094FB", textTransform: "none", mr: 1 }}>
            Download {form.slug}.lic
          </Button>
        )}

      </Box>
    );
  }

  return (
    <Box>
      <Typography fontWeight={600} fontSize="15px" mb={2}>Review & Confirm</Typography>
      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Typography fontSize="13px" fontWeight={600} color="#0094FB" mb={1}>Cloud & Deploy</Typography>
          <ReviewRow label="Organisation" value={form.org_name} />
          <ReviewRow label="Slug / Realm" value={form.slug} />
          <ReviewRow label="Hosting" value={form.hosting === "dedicated" ? "Dedicated (customer cloud)" : "Shared SaaS"} />
          <ReviewRow label="Cloud" value={form.cloud?.toUpperCase()} />
          <ReviewRow label="Region" value={form.region} />
          <ReviewRow label="Industry" value={form.industry} />
        </Grid>
        <Grid item xs={12} md={6}>
          <Typography fontSize="13px" fontWeight={600} color="#0094FB" mb={1}>Client Admin</Typography>
          <ReviewRow label="Name" value={`${form.admin_first_name} ${form.admin_last_name || ""}`.trim()} />
          <ReviewRow label="Email" value={form.admin_email} />
          <ReviewRow label="Username" value={form.admin_username || form.admin_email?.split("@")[0]} />
        </Grid>
        <Grid item xs={12} md={6}>
          <Typography fontSize="13px" fontWeight={600} color="#0094FB" mb={1}>Licensing</Typography>
          <ReviewRow label="Tier" value={form.tier?.charAt(0).toUpperCase() + form.tier?.slice(1)} />
          <ReviewRow label="Seats" value={String(form.seat_count)} />
          <ReviewRow label="Contract End" value={form.contract_end} />
          <ReviewRow label="On Expiry" value={
            form.expiry_behaviour === "warn" ? "Read-only mode"
              : form.expiry_behaviour === "grace" ? "7-day grace"
              : form.expiry_behaviour
          } />
          {(form.hosting === "dedicated" || form.cloud === "private") && form.deployment_fingerprint && (
            <ReviewRow label="Fingerprint" value={`${form.deployment_fingerprint.slice(0, 12)}…`} />
          )}
        </Grid>
      </Grid>
      {form.hosting === "dedicated" && (
        <Alert severity="info" sx={{ mt: 2 }}>
          A signed <strong>.lic</strong> file will be generated after you confirm for the customer cloud deploy.
        </Alert>
      )}
    </Box>
  );
}

// ─── Validation ───────────────────────────────────────────────────────────────

function isValidAdminPersonName(value) {
  const v = (value || "").trim();
  if (!/^[a-zA-Z0-9]+$/.test(v)) return false;
  // Digit-only values must be a positive integer (not 0).
  if (/^\d+$/.test(v)) return /^[1-9]\d*$/.test(v);
  return true;
}

function validate(step, form) {
  if (step === 0) {
    if (!form.hosting || !["shared", "dedicated"].includes(form.hosting)) {
      return "Please select Shared SaaS or Dedicated hosting.";
    }
    if (!form.cloud || !["aws", "gcp", "azure", "private"].includes(form.cloud)) {
      return "Please select a cloud provider (AWS, GCP, Azure, or Private Cloud).";
    }
    if (!form.org_name?.trim()) return "Organisation name is required.";
    if (!form.slug?.trim()) return "Realm slug is required.";
  }
  if (step === 1) {
    if (!form.admin_first_name?.trim()) return "Admin first name is required.";
    if (!isValidAdminPersonName(form.admin_first_name)) {
      return "First name must use letters (A–Z, a–z) and positive numbers only.";
    }
    if (!form.admin_last_name?.trim()) return "Last Name is required.";
    if (!isValidAdminPersonName(form.admin_last_name)) {
      return "Last name must use letters (A–Z, a–z) and positive numbers only.";
    }
    const email = form.admin_email?.trim();
    if (!email) return "Admin email is required.";
    const emailError = getEmailValidationError(email, { requireComplete: true });
    if (emailError) return emailError;
    const username = form.admin_username?.trim();
    if (username) {
      const usernameError = getUsernameValidationError(username);
      if (usernameError) return `Username: ${usernameError.toLowerCase()}.`;
    }
    if (!form.admin_temp_password?.trim()) return "Temporary password is required.";
  }
  if (step === 2) {
    if (!form.tier) return "Please select a licence tier.";
    const contractEndError = validateContractEnd(
      form.contract_end,
      form.contract_start || todayISODate(),
    );
    if (contractEndError) return contractEndError;
  }
  return null;
}

// ─── Main Wizard ──────────────────────────────────────────────────────────────

const INITIAL = {
  hosting: "shared", cloud: "aws", org_name: "", slug: "", region: "", industry: "",
  auth_mode: "standard", mfa_enabled: false, force_password_change: true, session_timeout_mins: 480,
  sso_idp_alias: "oidc", sso_client_id: "", sso_client_secret: "", sso_discovery_url: "",
  sso_allowed_domain: "", sso_display_name: "Sign in with your organisation",
  admin_first_name: "", admin_last_name: "", admin_email: "", admin_username: "", admin_temp_password: genPassword(),
  tier: "tenant", seat_count: 10, warning_threshold: 80,
  contract_start: todayISODate(), contract_end: "", expiry_behaviour: "lock",
  deployment_fingerprint: "",
};

export default function TenantOnboardingPage() {
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [form, setForm] = useState(INITIAL);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const [result, setResult] = useState(null);
  const [licenceKey, setLicenceKey] = useState(null);
const [errors, setErrors] = React.useState({
  org_name: false,
  slug: false,
  admin_first_name: false,
  admin_last_name: false,
  admin_email: false,
  admin_username: false,
});
const hasErrors = Object.values(errors).some(Boolean);
  const licenceStepInvalid = step === 2 && validateContractEnd(
    form.contract_end,
    form.contract_start || todayISODate(),
  );
  const set = (key, value) => setForm((p) => ({ ...p, [key]: value }));

  const handleNext = () => {
    const err = validate(step, form);
    if (err) { setError(err); return; }
    setError("");
    setStep((s) => s + 1);
  };

  const handleBack = () => { setError(""); setStep((s) => s - 1); };

  const handleSubmit = async () => {
    if (saving) return;
    const err = validate(step, form);
    if (err) { setError(err); return; }
    setSaving(true);
    setError("");
    try {
      const res = await axios.post(`${API_BASE_URL}/superadmin/tenants`, form);
      setResult(res.data);          // store full response (includes loginUrl, inviteEmailSent, etc.)
      setLicenceKey(res.data.licenceKey || null);
      setStep(3);
    } catch (e) {
      setError(e.response?.data?.error || "Failed to create tenant. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const stepContent = () => {
    switch (step) {
      case 0: return <StepCloud form={form} set={set} errors={errors} setErrors={setErrors} />;
      case 1: return <StepAdmin form={form} set={set} setErrors={setErrors} />;
      case 2: return <StepLicence form={form} set={set} />;
      case 3: return <StepReview form={form} result={result} licenceKey={licenceKey} />;
      default: return null;
    }
  };

  const isDone = step === 3 && result;

  return (
    <Box sx={{ bgcolor: "#f9fafb", px: 4, py: 3 }}>
      {/* Header */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 3 }}>
        <IconButton onClick={() => navigate("/platform/tenants")} size="small"
          sx={{ border: "1px solid #D9D9D9", borderRadius: 1 }}>
          <ArrowBackIcon fontSize="small" />
        </IconButton>
        <Box>
          <Typography variant="h5" fontWeight={700}>New Tenant Onboarding</Typography>
          <Typography variant="body2" color="text.secondary">
            Provision a new Keycloak realm + licence for a client organisation
          </Typography>
        </Box>
      </Box>

      {/* Stepper */}
      <Stepper activeStep={step} sx={{ mb: 4 }}>
        {STEPS.map((label, i) => (
          <Step key={label} completed={i < step}>
            <StepLabel>{label}</StepLabel>
          </Step>
        ))}
      </Stepper>

      {/* Content Card */}
      <Paper elevation={0} sx={{ border: "1px solid #e8e8e8", borderRadius: 2, p: 4, bgcolor: "#FFFFFF", minHeight: 360 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError("")}>{error}</Alert>
        )}
        {stepContent()}
      </Paper>

      {/* Navigation */}
      {!isDone && (
        <Box sx={{ display: "flex", justifyContent: "space-between", mt: 3 }}>
          <Button
            onClick={step === 0 ? () => navigate("/platform/tenants") : handleBack}
            sx={{ textTransform: "none", color: "#344054", border: "1px solid #D9D9D9", px: 3 }}
          >
            {step === 0 ? "Cancel" : "Back"}
          </Button>

          {step < 3 && (step < 2 ? (
            
            <Button  disabled={hasErrors} variant="contained" onClick={handleNext} endIcon={<ArrowForwardIcon />}
              sx={{ bgcolor: "#0094FB", "&:hover": { bgcolor: "#0077cc" }, textTransform: "none", px: 3 }}>
              Next
            </Button>
          ) : (
            <Button variant="contained" onClick={handleSubmit} disabled={saving || Boolean(licenceStepInvalid)}
              sx={{ bgcolor: "#0094FB", "&:hover": { bgcolor: "#0077cc" }, textTransform: "none", px: 4 }}>
              {saving ? <CircularProgress size={20} sx={{ color: "#fff" }} /> : "Create Tenant"}
            </Button>
          ))}
        </Box>
      )}

      {isDone && (
        <Box sx={{ display: "flex", justifyContent: "center", mt: 3 }}>
          <Button variant="outlined" onClick={() => navigate("/platform/tenants")}
            sx={{ color: "#0094FB", borderColor: "#0094FB", textTransform: "none", px: 4 }}>
            Back to Tenant Management
          </Button>
        </Box>
      )}
    </Box>
  );
}